import React, { useState, useContext, useEffect, useCallback } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslator } from '@jutro/locale';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { ViewModelForm, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { PolicyService } from 'gw-capability-policy';
import { MLAlertGroupComponent} from 'ml-components-common-react';
import { MLTagManagerService } from 'ml-portals-services-react';
import appConfig from 'app-config';

import metadata from './MLDocumentsUploadPopup.metadata.json5';
import styles from './MLDocumentsUploadPopup.module.scss';
import messages from './MLDocumentsUploadPopup.messages'

const MLDocumentsUploadPopup = (props) => {
    const {
        policyNumber,
        policy,
        policies = [],
        contingencyId,
        onCancel,
        onSave,
        viewModelService,
        authHeader
    } = props;

    const { refreshData } = useDataRefresh();
    const translator = useTranslator();

    const { onValidate, isComponentValid } = useValidation('MLDocumentsUploadPopup');
    const { unacceptableFileExt, maxFileUploadSize } = appConfig
    const [documentVM, updateDocumentVM] = useState({});
    const [documentSubTypes, updateDocumentSubTypes] = useState([]);

    const [documentData, updateDocumentData] = useState({});
    const [currentPolicyOption, updateCurrentPolicyOptions] = useState({});
    const [policyOptionLists, updatePolicyOptionLists] = useState([]);
    const [isPolicyOptionListsInitialize, setIsPolicyOptionListsInitialize] = useState(false);
    const documentSubtypeTypelist = viewModelService.productMetadata.get('pc').types.getTypelist('DocumentSubType_ML');
    const [errorMessage, setErrorMessage] = useState('');
    const [isFailUploadFile, setIsFailUploadFile] = useState(false);
    const [isOtherOption, setIsOtherOption] = useState(false);
    const [successfullUpload, setSuccessfullUpload] = useState(false);
    const [showErrors, setShowErrors] = useState(false);

    const pcUploadServiceName = 'policy/docupload';
    const RECURRING_CC_AUTHORIZATION = 'recurring_credit_or_debit_card_authorization';
    const RECURRING_EFT_AUTHORIZATION = 'recurring_eft_authorization';

    const matchDocSubTypeFilter = (policyPeriod) => {
        switch (policyPeriod.lines[0]) {
            case 'HOPLine':
                return 'dex_doc_type_home';
            case 'PersonalAutoLine':
                return 'dex_doc_type_auto';
            default:
                return 'dex_doc_type_common';
        }
    };

    const getPolicyOption = useCallback((polNumber) => {
        return policyOptionLists.find((option) => option.policyNumber === polNumber);
    },[policyOptionLists]);

    const getFormContingenciesDocSubType = (contingencies) => {
        return contingencies.filter((contingency)=> contingency.docSubType_ML !== RECURRING_CC_AUTHORIZATION
            && contingency.docSubType_ML !== RECURRING_EFT_AUTHORIZATION)
            .map((contingency) => contingency.docSubType_ML);
    };

    const initPolicyOptions = useCallback(async () => {
        // assignment to policyoption
        // For the doc upload link on the AMP policy page
        if (policies && policies.length > 0) { // AMP
            if (!isPolicyOptionListsInitialize) {
                const policyOptions = policies.map((policySummary) => {
                    return {
                        policyNumber: policySummary.periods[0].policyId,
                        contingencyFormSubTypes_ML: getFormContingenciesDocSubType(policySummary.formContingencies_ML),
                        documentSubTypeFilterName_ML: matchDocSubTypeFilter(policySummary.periods[0])
                    }
                })
                updatePolicyOptionLists(policyOptions)
                setIsPolicyOptionListsInitialize(true);
            }

            updateCurrentPolicyOptions(getPolicyOption(policyNumber));
        }
        
    },[getPolicyOption, isPolicyOptionListsInitialize, policies, policyNumber])

    useEffect(() => {
        const subtypeVal = _.get(documentVM.value, 'documentSubType_ML');
        subtypeVal ? setIsOtherOption(subtypeVal.includes('other')) : setIsOtherOption(false) 
    }, [documentVM.value])
    
    const getPolicyPeriodByPolicyNumber = useCallback((polNum) => {
        return _.find(policies, (pol) => {
            return pol.periods[0].policyId === polNum
        })
    },[policies])

    const initDocSubTypeFilterValue = (docSubtypeTypelist, docfilterType) => {
        return _.find(docSubtypeTypelist.filters, 
            (documentSubtypeFilter) => { 
                return documentSubtypeFilter.name === docfilterType
            }).codes
    }

    const getFormContingencies = (polNum) => {
        return currentPolicyOption.find((option) => option.policyNumber === polNum).formContingencies;
    };

    const getDocSubTypeFilter = (polNum) => {
        return currentPolicyOption.find((option) => option.policyNumber === polNum).docSubTypeFilterName;
    };

    const getContingenciesDocSubTypeFilterValue = (contdocSubtypeTypelist, contdocfilterType) => {
        return contdocSubtypeTypelist.codes.filter((item) => contdocfilterType.includes(item.code));

    }

    const getCombinedDocSubTypeFilterValue = (contdocSubtypeTypelist, contdocfilterType) => {
        return _.sortBy([...new Set(contdocSubtypeTypelist.concat(contdocfilterType))], 'code');
    }


    const defineDocSubtype = (policyData) => {
        const productName = policyData.product_ML.productCode
        switch (productName) {
            case 'PersonalAuto':
                return 'dex_doc_type_auto';
            case 'Homeowners':
                return 'dex_doc_type_home';
            default:
                return 'dex_doc_type_common';
        }
    }

    const createVM = useCallback(
        (model) => {
            return viewModelService.create(
                model,
                'pc',
                'edge.capabilities.policy.document.dto.PolicyDocumentUploadDTO'
            );
        },
        [viewModelService]
    );

    const initDocumentVM = useCallback((val) => {
        const policySummary = getPolicyPeriodByPolicyNumber(val)
        const tempOption = getPolicyOption(val) 
        const doc = createVM(tempOption);
        updateDocumentVM(doc);
        const lobAlwaysDocumentSubTypes = (initDocSubTypeFilterValue(documentSubtypeTypelist, matchDocSubTypeFilter(policySummary.periods[0])));
        const contingenciesDocumentSubTypes = (getContingenciesDocSubTypeFilterValue(documentSubtypeTypelist, getFormContingenciesDocSubType(policySummary.formContingencies_ML)));
        const lobAlwaysAndContingenciesDocSubTypes = (getCombinedDocSubTypeFilterValue(lobAlwaysDocumentSubTypes, contingenciesDocumentSubTypes));
        updateDocumentSubTypes(lobAlwaysAndContingenciesDocSubTypes)
    },[createVM, documentSubtypeTypelist, getPolicyOption, getPolicyPeriodByPolicyNumber])
    
    useEffect(() => {
        // if its AMP portal fill up with these
        initPolicyOptions().catch(console.error);
        initDocumentVM(policyNumber)
    }, [initPolicyOptions, initDocumentVM, policyNumber]);

    

    const handleChange = useCallback(
        (value, path) => {
            const newDocumentSubmissionVM = viewModelService.clone(documentVM);
            _.set(newDocumentSubmissionVM.value, path, value !== '' ? value : undefined);
            switch (path) {
                case 'type':
                    _.set(newDocumentSubmissionVM, 'type', value);
                    break;
                case 'policy':
                    const tempOption = getPolicyOption(value);
                    const policySummary = getPolicyPeriodByPolicyNumber(value)
                    _.set(newDocumentSubmissionVM, 'policyNumber', value);
                    _.set(newDocumentSubmissionVM, 'contingencyFormSubTypes_ML', tempOption.contingencyFormSubTypes_ML);
                    _.set(newDocumentSubmissionVM, 'documentSubTypeFilterName_ML', tempOption.documentSubTypeFilterName_ML);
                    const chgLobAlwaysDocumentSubTypes = (initDocSubTypeFilterValue(documentSubtypeTypelist, matchDocSubTypeFilter(policySummary.periods[0])));
                    const chgContingenciesDocumentSubTypes = (getContingenciesDocSubTypeFilterValue(documentSubtypeTypelist, getFormContingenciesDocSubType(policySummary.formContingencies_ML)));
                    const chgLobAlwaysAndContingenciesDocSubTypes = (getCombinedDocSubTypeFilterValue(chgLobAlwaysDocumentSubTypes, chgContingenciesDocumentSubTypes));
                    updateDocumentSubTypes(chgLobAlwaysAndContingenciesDocSubTypes)
                    break;
                default:
                    break;
            }
            updateDocumentVM(newDocumentSubmissionVM);
            refreshData();
        },
        [documentVM, refreshData, viewModelService]
    );

    const onUploadDocument = useCallback(
        (file) => {
            updateDocumentData(file)
        },
        []
    );

    const onDocumentChange = () => {
        if (documentData) {
            updateDocumentData({});
        }
    }

    const transformAvailableValues = (values) => {
        return values.map((value) => {
            return {
                code: value.code,
                name: translator({
                    id: value.name,
                    defaultMessage: value.name
                })
            }
        })
    }

    const transformPolicyPeriodId = (values) => {
        return values.map((value) => {
            return {
                code: value.periods[0].policyId,
                name: translator({
                    id: value.periods[0].policyId,
                    defaultMessage: value.periods[0].policyId
                })
            }
        })
    }

    const [isUploading, setIsUploading] = useState(false);

    const handleSave = async () => { 

        if (!isComponentValid) {
            setShowErrors(true);
            return;        
        }

        if (!(documentData && documentData.name && documentData.type) || isUploading) {
            return;
        }

        const documentUploadTokenId = await PolicyService.getDocumentUploadToken(authHeader);

        const fileName = documentData.name;
        const tmpArray = fileName.split('.');
        let fileExt = {};
            
        if (tmpArray.length > 0) {
            fileExt = tmpArray[tmpArray.length - 1];
        }
        const acceptableFileExt = fileExt && !unacceptableFileExt.includes(fileExt);
        const fileSizeValid = documentData.size <= maxFileUploadSize;

        if(acceptableFileExt && fileSizeValid) {
            const newDocumentMetadata = {
                policyNumber: _.get(documentVM.value, 'policyNumber'),
                xCenter: 'pc',
                uploadingDoc: pcUploadServiceName,
                documentType: _.get(documentVM.value, 'type'),
                ContingencyFormSubTypes_ML: _.get(documentVM.value,'contingencyFormSubTypes_ML'),
                DocumentSubTypeFilterName_ML: _.get(documentVM.value,'documentSubTypeFilterName_ML'),
                DocumentSubType_ML: _.get(documentVM.value, 'documentSubType_ML'),
                contingencyId_ML: contingencyId,
                name: documentData.name,
                mimeType: documentData.type,
                sessionID: documentUploadTokenId,
                status: 'approved',
            }
    
            try{
                const formValues = {
                    type: _.get(documentVM, 'documentSubType_ML.value.name')
                };
                MLTagManagerService.sendFormCompleteEvent(`${MLTagManagerService.getFormName()}_Upload Documents`, formValues);
                setIsUploading(true)
                await (PolicyService.uploadDocument(documentData, newDocumentMetadata, authHeader))
                setSuccessfullUpload(true);
                updateDocumentData({});
                setIsFailUploadFile(false);
            } catch (error) {
                setErrorMessage(translator(messages.documentUploadFail, { fileName: fileName }))
                setIsFailUploadFile(true)
            } finally {
                setIsUploading(false);
            }
                
        } else {
            setErrorMessage(acceptableFileExt ? messages.invalidFileSize : messages.invalidFileType)
            setIsFailUploadFile(true)
        } 
    }

    const overrideProps = {
        uploadPopupHeader: {
            className: "vertical-spacing-40"
        },
        warningInlineNotification: {
            message: translator(messages.whenYouHaveMoreThanOneDocument)
        },
        successInlineNotification: {
            message: translator(messages.documentUploadedSuccessfully, {
                strong: (chunks) => <strong>{chunks}</strong>
            }),
            visible: successfullUpload
        },
        errorInlineNotification: {
            message: errorMessage,
            visible: isFailUploadFile
        },
        type: {
            availableValues: [{name: 'Policy Document', code: 'Policy Document'}],
            value: _.get(documentVM.value, 'type')
        },
        subtype: {
            availableValues: transformAvailableValues(documentSubTypes),
        },
        policy: {
            visible: !contingencyId,
            availableValues: transformPolicyPeriodId(policies),
            value: _.get(documentVM.value, 'policyNumber')
        },
        uploadedFileNameContainer: {
            visible: !_.isUndefined(documentData),
            content: documentData.name
        },
        closeButton: {
            content: translator(messages.cancelModal)
        },
        saveButton : {
            content: translator(messages.continue)
        }
    };

    const resolvers = {
        resolveComponentMap: {
            MLAlertGroupComponent
        },
        resolveCallbackMap: {         
            onClose: onCancel,
            onSave: handleSave,
            onUploadDocument: onUploadDocument,
            onDocumentChange: onDocumentChange
        },
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            model={documentVM}
            onValueChange={handleChange}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={styles}
            onValidationChange={onValidate}
            showErrors={showErrors}
        />
    );
}


MLDocumentsUploadPopup.propTypes = {
    policy: PropTypes.shape({}),
    policyNumber: PropTypes.string.isRequired,
    contingencyId: PropTypes.string
}

export default MLDocumentsUploadPopup;